import EventEmitter from 'events'
import { createContext, ReactNode, useCallback, useState } from 'react'

// Eager inputs are similar to uncontrolled inputs, but they accept a `lazyValue`,
// which should be the "backend-synced" value. This is useful when we don't want the
// component to rely on external state (e.g. a tanstack query), because that could
// interact with the user interacting with the input; instead, the input will hold
// its own state, but we can later check to make sure the external value matches that
// inner state. The `lazyValue` will never be used to render.

// @deprecated
export type EagerInput<T> = {
	debugId: string
	lazyValue: T
	onChange: (value: T) => void
	defaultValue: T
}

const UncontrolledCheckerContext = createContext<{
	emitter: EventEmitter
}>({
	emitter: new EventEmitter(),
})

export function UncontrolledCheckerWrapper({ children }: { children: ReactNode }) {
	const emitter = new EventEmitter()

	return <UncontrolledCheckerContext.Provider value={{ emitter }}>{children}</UncontrolledCheckerContext.Provider>
}

type UseEagerInputReturns<T> = {
	value: T
	onChange: (value: T) => void
}

export function useEagerInput<T>(
	debugId: string,
	defaultValue: T,
	onChange: (value: T) => void,
	comparator: (a: T, b: T) => boolean = (a, b) => a === b,
) {
	// hook into context, allow checking true value vs defaultValue

	const [value, setValue] = useState(defaultValue)

	const handleChange = useCallback((val: T) => {
		setValue(val)
		onChange(val)
	}, [onChange])

	return {
		value,
		onChange: handleChange,
	}
}
